<template>
  <div class="a-line">
    <canvas :id="id" class="a-line__canvas" :height="height" />
  </div>
</template>

<script>
import Chart from 'chart.js/auto';

// Pineapple line palette — first 2 entries used most: yellow (primary) + blue (secondary)
const SERIES_COLORS = [
  { stroke: '#C98A0A', point: '#C98A0A', fillTop: 'rgba(255, 201, 60, 0.28)', fillBottom: 'rgba(255, 201, 60, 0)' },   // yellow
  { stroke: '#3B7BC4', point: '#3B7BC4', fillTop: 'rgba(59, 123, 196, 0.20)', fillBottom: 'rgba(59, 123, 196, 0)' },   // blue
  { stroke: '#1E8C66', point: '#1E8C66', fillTop: 'rgba(30, 140, 102, 0.20)', fillBottom: 'rgba(30, 140, 102, 0)' },   // green
];

export default {
  name: 'ALineChart',
  props: {
    id:     { type: String, default: 'a-line-chart' },
    height: { type: [String, Number], default: 280 },
    chart:  { type: Object, required: true },
    fill:   { type: Boolean, default: true },
  },
  data() {
    return { chartInstance: null };
  },
  watch: {
    'chart.datasets': { handler() { this.render(); }, deep: true },
    'chart.labels':   { handler() { this.render(); }, deep: true },
  },
  mounted() {
    this.render();
  },
  beforeUnmount() {
    if (this.chartInstance) this.chartInstance.destroy();
  },
  methods: {
    render() {
      const el = document.getElementById(this.id);
      if (!el) return;
      const ctx = el.getContext('2d');

      const existing = Chart.getChart(this.id);
      if (existing) existing.destroy();

      const datasets = (this.chart.datasets || []).map((ds, i) => {
        const palette = SERIES_COLORS[i % SERIES_COLORS.length];
        let bg = palette.fillTop;
        if (this.fill) {
          const grad = ctx.createLinearGradient(0, 0, 0, 240);
          grad.addColorStop(0, palette.fillTop);
          grad.addColorStop(1, palette.fillBottom);
          bg = grad;
        }
        return {
          label: ds.label,
          data: ds.data,
          tension: 0.35,
          borderColor: palette.stroke,
          borderWidth: 2.5,
          pointBackgroundColor: palette.point,
          pointBorderColor: '#FFFFFF',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          backgroundColor: bg,
          fill: this.fill,
        };
      });

      this.chartInstance = new Chart(ctx, {
        type: 'line',
        data: {
          labels: this.chart.labels,
          datasets,
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { intersect: false, mode: 'index' },
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: '#1F1B14',
              titleColor: '#FFC93C',
              titleFont: { weight: '700', size: 12 },
              bodyColor: '#FFFFFF',
              bodyFont: { size: 12 },
              padding: 10,
              cornerRadius: 8,
              displayColors: true,
              boxPadding: 6,
            },
          },
          scales: {
            y: {
              grid: {
                color: '#F0E8D2',  // $p-line-soft
                drawBorder: false,
                drawTicks: false,
                borderDash: [4, 4],
              },
              ticks: {
                color: '#9C9486',  // $p-ink-soft
                font: { family: 'JetBrains Mono, monospace', size: 10, weight: '600' },
                padding: 8,
              },
              border: { display: false },
            },
            x: {
              grid: {
                color: '#F0E8D2',
                drawBorder: false,
                drawTicks: false,
                borderDash: [4, 4],
              },
              ticks: {
                color: '#9C9486',
                font: { family: 'JetBrains Mono, monospace', size: 10, weight: '600' },
                padding: 6,
              },
              border: { display: false },
            },
          },
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.a-line {
  position: relative;
  width: 100%;
}
.a-line__canvas {
  max-width: 100%;
}
</style>
